import { effect, inject, Injectable, WritableSignal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { getState } from '@ngrx/signals';
import { filter } from 'rxjs';
import { Step } from '../models/model/concretes/components/step.model';
import { NavigationService } from './navigation.service';
import { SharedDataService } from './shared-data.service';
import { Subscription } from '../models/model/concretes/shared/subcription.model';
import { CombinedState, CombinedStore } from 'src/app/core/combined.store';
import { environment } from 'src/environments/environment';
import { isPartner } from '../utils/brand-utils';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  currentStep: Step | undefined;
  currentGroupStepIndex: WritableSignal<number>;
  currentStepIndex: WritableSignal<number>;

  subscription = this.sharedDataService.getSubscription();

  store = inject(CombinedStore);

  // Journey answers as they were when each step was validated, in validation order
  // shortcut: kept in memory, a page reload disables the reset on back until steps are validated again; persist in the store if that matters
  private stepSnapshots = new Map<string, Partial<CombinedState>>();

  constructor(
    private sharedDataService: SharedDataService,
    private navigationService: NavigationService,
    private router: Router
  ) {
    this.currentGroupStepIndex = this.sharedDataService.getGroupStep();
    this.currentStepIndex = this.sharedDataService.getStepIndex();

    effect(
      () => {
        const index = this.sharedDataService.getStepIndex();
        if (index != null) {
          this.updateCurrentStep();
        }
      },
      { allowSignalWrites: true }
    );

    // Steps are only navigated forward to unvalidated steps: reaching a validated one (breadcrumb, progress circle,
    // browser back, "Précédent") is going back to it
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(event => this.forgetStepsAfter((event as NavigationStart).url.split('/')[2] ?? ''));
  }

  //#region Public Methods
  previousBtn(): boolean {
    return this.currentStepIndex() != 0;
  }

  previousStep() {
    if (this.currentStep != undefined && this.currentStepIndex() != 0) {
      this.sharedDataService.previousStep();
      this.updateCurrentStep();
    }
  }

  nextBtn(): boolean {
    return (
      this.currentGroupStepIndex() != (this.subscription()?.groupSteps?.length ?? 0) - 1 ||
      this.currentStepIndex() !=
        (this.subscription()?.getStepsCountInGroup(this.currentGroupStepIndex()) ?? 0) - 1
    );
  }

  nextStep() {
    this.navigationService.updateSubmitted(false);

    if (this.currentStep) {
      this.currentStep.isActive = false;
      this.currentStep.isCompleted = true;
      this.subscription()?.setCurrentStep(
        this.currentGroupStepIndex(),
        this.currentStepIndex(),
        this.currentStep
      );
      this.saveStepSnapshot(this.currentStep.url);
    }

    this.sharedDataService.forceUpdateSubscription();

    const stepCount =
      (this.subscription()?.getStepsCountInGroup(this.currentGroupStepIndex()) ?? 0) - 1;
    if (this.currentStepIndex() <= stepCount) {
      if (this.currentStepIndex() == stepCount) {
        this.sharedDataService.nextGroupStep();
        this.resetStepOfGroupStep();
      } else {
        this.sharedDataService.nextStep();
      }

      this.updateCurrentStep();
    }
  }

  /** Going back to a validated step forgets everything answered since it was validated. */
  forgetStepsAfter(stepUrl: string) {
    const snapshot = this.stepSnapshots.get(stepUrl);
    if (!snapshot) return;

    const validatedUrls = [...this.stepSnapshots.keys()];
    const forgottenUrls = validatedUrls.slice(validatedUrls.indexOf(stepUrl));
    forgottenUrls.forEach(url => this.stepSnapshots.delete(url));
    this.subscription()
      ?.getGroupSteps()
      .flatMap(groupStep => groupStep.steps)
      .filter(step => step.url !== stepUrl && forgottenUrls.includes(step.url))
      .forEach(step => (step.isCompleted = false));

    this.store.update(snapshot);
  }
  //#endregion

  //#region Private Methods
  public updateCurrentStep() {
    //Mettre à jour l'étape courante avant de passer à l'étape suivante
    this.currentStep = this.subscription()?.getCurrentStep(
      this.currentGroupStepIndex(),
      this.currentStepIndex()
    );

    if (this.currentStep) {
      this.currentStep.isActive = true;
      this.subscription()?.setCurrentStep(
        this.currentGroupStepIndex(),
        this.currentStepIndex(),
        this.currentStep
      );

      this.subscription()?.activateStepByUrl(this.currentStep.url);
    }

    // Ne pas afficher l'étape "Information" du Client si le parcours est issu d'une Fiche Client
    // Afficher directement l'étape suivante
    if (this.currentStep?.url == "subscriber" && this.store.context().souscripteur?.email && !isPartner(environment)) {
      this.nextStep();
      return;
    }

    if (this.currentStep?.layoutInfo) {
      this.setHeader(this.currentStep);
      this.navigationService.setNavigationState({
        groupIndex: this.currentGroupStepIndex(),
        stepNumber: this.currentStepIndex(),
        isStepValid: false,
        subscriberCGVm: null,
        url: this.currentStep.url
      });

      this.router.navigate(['subscription', this.currentStep.url]);
    }
  }

  private setHeader(step: Step) {
    this.sharedDataService.setLayoutInfo(step.layoutInfo);
    this.sharedDataService.setStepShow(step.layoutInfo.stepShow);
    this.setMaxStep();
    this.setMaxStepShow();
  }

  public setMaxStep() {
    const groupStepIndex = this.currentGroupStepIndex();
    if (groupStepIndex !== undefined) {
      this.sharedDataService.setMaxStep(
        this.subscription()?.getStepsCountInGroup(groupStepIndex) ?? 0
      );
    }
  }

  private setMaxStepShow() {
    let maxStep = this.subscription()?.getLastStepInGroupStep(this.currentGroupStepIndex());
    if (maxStep) this.sharedDataService.setMaxStepShow(maxStep);
  }

  private resetStepOfGroupStep() {
    this.sharedDataService.setStep(1);
    this.sharedDataService.setStepIndex(0);
  }

  private saveStepSnapshot(stepUrl: string) {
    // Subscriber, IBAN, quotation and context are not journey answers: they survive going back
    const { path, declaration, offer } = getState(this.store);
    this.stepSnapshots.delete(stepUrl); // re-inserted to keep the validation order
    this.stepSnapshots.set(stepUrl, { path, declaration, offer });
  }
  //#endregion
}

